<template>
  <ul class="list-of-member-products">
    <LazyLoadWhenInViewport
      v-for="index in shouldShowMoreButton ? INITIAL_VISIBLE_ITEM_COUNT : list.length"
      :key="getMemberId(index)"
      tag="li"
      :disable-observer="
        // disable observer in SSR or first 10 list items after hydration (fixes template bug)
        !enableClientSideMemberListPerfImprovement || (!hasListChanged && !isMounted && getCorrectedIndex(index) < INITIAL_VISIBLE_ITEM_COUNT)
      "
      :root-margin="2400"
      :with-transition="enableClientSideMemberListPerfImprovement && hasListChanged && isMounted && getCorrectedIndex(index) > 1"
      class="member-product-item grid-y"
    >
      <template #default>
        <Suspense>
          <component :is="MemberProduct" :product-id="getMemberId(index)" />
          <template #fallback>
            <slot name="skeleton-loaders" :is-gift="isGiftWithPurchase" />
          </template>
        </Suspense>
      </template>
      <template #fallback>
        <slot name="skeleton-loaders" :is-gift="isGiftWithPurchase" />
      </template>
    </LazyLoadWhenInViewport>
  </ul>

  <button
    v-if="shouldShowMoreButton"
    class="show-more-members secondary button expanded small-margin-bottom-xl medium-margin-bottom-xxl margin-top-m"
    @click="showMoreMemberProducts"
  >
    Show {{ moreItemsCount }} More Items
  </button>
</template>

<script lang="ts" setup>
import { ref, computed, watch, defineAsyncComponent, markRaw, type AsyncComponentLoader } from 'vue';
import { useMounted } from '@vueuse/core';
import { defineLazyVisibleHydratedComponent } from '@nuxt-common/media-card-large/app/modules/lazy-hydration';

import { useProductType } from '~/composables/useProductType';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import LazyLoadWhenInViewport from '~/components/common/NewLazyLoadWhenInViewport.vue';

type NonEmptyArray<T> = [T, ...T[]];

type PropType = {
  list: NonEmptyArray<string>;
};

/** The number of member products to display before the user clicks "Show More". */
const INITIAL_VISIBLE_ITEM_COUNT = 10 as const;

/**
 * A map to dynamically import member product components.
 * This allows for code-splitting, so the component code is only loaded when needed.
 */
const MEMBER_COMPONENT_MAP = {
  'member-product': () => import('~/components/ProductCard/MemberProduct.vue'),
  'gift-member-product': () => import('~/components/ProductCard/GiftMemberProduct.vue'),
} as const;

/** A flag to determine if the component is mounted or not. */
const isMounted = useMounted();

const { list } = defineProps<PropType>();

const { isGiftWithPurchase } = useProductType();

const {
  killswitches: { enableClientSideMemberListPerfImprovement, isLazyHydrationEnabled },
} = useKillswitches();

const memberProductComponentLoader: AsyncComponentLoader = MEMBER_COMPONENT_MAP[isGiftWithPurchase ? 'gift-member-product' : 'member-product'];
const asyncCompLoader = isLazyHydrationEnabled ? defineLazyVisibleHydratedComponent : defineAsyncComponent;
/**
 * Asynchronously and dynamically loads the appropriate member product component based on the product type.
 * If the product is a gift, it loads `GiftMemberProduct.vue`; otherwise, it loads `MemberProduct.vue`.
 *
 * `markRaw` is used to prevent Vue from making the async component reactive, which is a performance optimization.
 * This is safe because the component itself doesn't need to be reactive.
 */
const MemberProduct = markRaw(asyncCompLoader(memberProductComponentLoader));

/** Determines if the "Show More" button should be visible. */
const shouldShowMoreButton = ref<boolean>(false);
const hasListChanged = ref<boolean>(false);

/** Computes the number of items that are hidden and will be shown when "Show More" is clicked. */
const moreItemsCount = computed<number>(() => list?.length - INITIAL_VISIBLE_ITEM_COUNT);

/**
 * Converts a 1-based index from the `v-for` loop to a 0-based index for array access.
 * @param num The 1-based index.
 * @returns The corresponding 0-based index.
 */
const getCorrectedIndex = (num: number): number => num - 1;

/**
 * Retrieves the product ID for a given 1-based index.
 * @param num The 1-based index from the `v-for` loop.
 * @returns The product ID string.
 */
const getMemberId = (num: number): (typeof list)[number] => list[getCorrectedIndex(num)] as string;

/**
 * Handles the click event on the "Show More" button.
 * It hides the button, which in turn causes the `v-for` loop to render all items in the list.
 */
const showMoreMemberProducts = () => {
  shouldShowMoreButton.value = false;
};

/**
 * Watches for changes to the `list` prop to control the visibility of the "Show More" button.
 * - On the first list change (e.g., after initial client-side hydration), it sets `hasListChanged` to true.
 *   This helps manage lazy-loading behavior.
 * - It shows the "Show More" button only if the total number of items exceeds the initial visible count.
 */
watch(
  () => list,
  (newList, prevList) => {
    // `hasListChanged` is used to enable the intersection observer for items beyond the initial count.
    if (prevList && !hasListChanged.value) {
      // TODO: remove temp bug fix - find a more suitable solution for page load
      hasListChanged.value = true;
    }
    shouldShowMoreButton.value = newList.length > INITIAL_VISIBLE_ITEM_COUNT;
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.button.expanded.show-more-members {
  display: block;
  margin: auto;
}

.list-of-member-products {
  list-style: none;
  margin: 0;
  padding: 0;
}
@include breakpoint(medium up) {
  .show-more-members {
    max-width: 335px;
  }
}
</style>

<style lang="scss">
@import '~settingSCSS';
.list-of-member-products {
  list-style: none;

  li.member-product-item {
    height: 100%;
    border-bottom: 1px solid $gray-3-color;

    & > div {
      flex: 1;
    }

    &:not(:first-child) {
      @include breakpoint(small only) {
        @include margin-top-xl;
      }
      @include margin-top-m;
    }
  }
}
</style>
